import { FC, useMemo, useState } from 'react';
import { Task } from '@repo/shared';
import {
  ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';

import { LeaderboardResponse } from '@/api/generated.schemas';
import { DataTable } from '@/components/blocks/data-table/data-table';
import { columns as describeImageColumns } from '@/routes/(app)/(leaderboards)/components/describe-image-leaderboard-columns';
import { columns as extractStructureColumns } from '@/routes/(app)/(leaderboards)/components/extract-structure-leaderboard-columns';
import { columns as audioGenerationColumns } from '@/routes/(app)/(leaderboards)/components/generate-audio-leaderboard-columns';
import { columns as translateColumns } from '@/routes/(app)/(leaderboards)/components/text-translation-leaderboard-columns';
import { LeaderboardItem } from '@/routes/(app)/(leaderboards)/lib/types';

interface LeaderboardTableProps {
  data: LeaderboardResponse;
}

export const LeaderboardTable: FC<LeaderboardTableProps> = ({ data }) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: 'numberOfModelRuns',
      desc: true,
    },
  ]);

  const columns: Array<ColumnDef<LeaderboardItem>> = useMemo(() => {
    switch (data.data.task) {
      case Task.GENERATE_AUDIO:
        return audioGenerationColumns;
      case Task.TEXT_TRANSLATION:
        return translateColumns;
      case Task.DESCRIBE_IMAGE:
        return describeImageColumns;
      case Task.EXTRACT_STRUCTURE:
        return extractStructureColumns;
      default:
        throw new Error('Unsupported task');
    }
  }, [data]) as Array<ColumnDef<LeaderboardItem>>;
  const table = useReactTable<LeaderboardItem>({
    state: {
      pagination,
      sorting,
    },
    getRowId: (row) => row.model,
    data: data.data.items as Array<LeaderboardItem>,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    pageCount: Math.ceil(data.data.items.length / pagination.pageSize),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
  });

  return <DataTable table={table} />;
};
